import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ScriptValueDialog as NewScriptValueDialog } from '@microservices/wiskey-react-components'
import { Grid, SxProps, Theme, Typography } from '@mui/material'

import { ScriptValueDialog } from '@components/ScriptValueDialog'

import { useFetchObjectByCodeQuery } from '@redux/api'
import { useFetchCommandParametersQuery } from '@redux/api/command.api'

import { MODAL_TYPE, TYPES_SOURCE_OPTIONS } from '@constants'
import { ActionJSScriptConfig, AutocompleteOption, CommandParametersType } from '@types'

import { CommandConfigTable } from './components/CommandConfigTable'
import { BODY_VIEW } from './components/CommandConfigTable/consts'
import { mergeAvailableWithConfigured } from './helpers'

type CommandParametersProps = {
  name: string
  parametersMode?: 'params' | 'body'
  sourceCode: string
  sourceTitle: string
  typeSource: TYPES_SOURCE_OPTIONS | undefined
  fieldsObjectCode?: string
  fieldsObjectTitle?: string
  commandCode: string
  label?: string
  labelWidth?: number | string
  containerSx?: SxProps<Theme>
  canChangeRefObject?: boolean
  refObjectOptions?: AutocompleteOption[]
  disableFieldType?: boolean
  jsScriptConfig?: ActionJSScriptConfig
}

export const CommandParameters: FC<CommandParametersProps> = ({
  name,
  parametersMode = 'params',
  typeSource = TYPES_SOURCE_OPTIONS.OBJECT,
  sourceCode,
  sourceTitle,
  fieldsObjectCode,
  fieldsObjectTitle = '',
  commandCode,
  label,
  labelWidth = 150,
  containerSx = {},
  canChangeRefObject = false,
  refObjectOptions = [],
  disableFieldType,
  jsScriptConfig,
}) => {
  const { t } = useTranslation()

  const { control, setValue, getValues, clearErrors, reset, resetField } = useFormContext()

  const { fields } = useFieldArray({
    control,
    name: name as any,
    rules: {},
  })

  const [bodyView, setBodyView] = useState<BODY_VIEW>(BODY_VIEW.ALL)
  const [isScriptDialogOpen, setScriptDialogOpen] = useState(false)
  const [jsRowIndex, setJsRowIndex] = useState<number | null>(null)

  const args = useMemo(
    () => ({ objectCode: sourceCode, commandCode, typeSource: typeSource.toLowerCase() }),
    [sourceCode, commandCode, typeSource]
  )

  const {
    currentData: commandParameters,
    isLoading: isLoadingCommandParameters,
    isFetching: isFetchingCommandParameters,
  } = useFetchCommandParametersQuery(args, {
    skip: !sourceCode || !commandCode,
    refetchOnMountOrArgChange: true,
  })

  const {
    currentData: objectOrSystemModel,
    isLoading: isLoadingObjectOrSystemModel,
    isFetching: isFetchingObjectOrSystemModel,
  } = useFetchObjectByCodeQuery(commandParameters?.body, {
    skip:
      !commandCode ||
      parametersMode === 'params' ||
      commandParameters?.httpMethod === 'GET' ||
      !commandParameters?.body,
    refetchOnMountOrArgChange: true,
  })

  const isLoading =
    isLoadingCommandParameters ||
    isFetchingCommandParameters ||
    isLoadingObjectOrSystemModel ||
    isFetchingObjectOrSystemModel

  const defaultRefFieldsObjectOption = useMemo(() => {
    if (fieldsObjectCode)
      return { id: fieldsObjectCode, label: fieldsObjectTitle || fieldsObjectCode }
    if (sourceCode) return { id: sourceCode, label: sourceTitle || sourceCode }

    return null
  }, [fieldsObjectCode, fieldsObjectTitle, sourceCode, sourceTitle])

  const availableRows = useMemo(() => {
    const buildRowKey = (name?: string | null) => (name ?? '').trim()
    if (parametersMode === 'params') {
      return (
        commandParameters?.params?.map(param => ({
          key: buildRowKey(param.name),
          name: param.name,
          description: param.description ?? null,
          isRequired: param.isRequired ?? null,
          canBeOverridden: param.canBeOverridden ?? null,
          valueTypeCode: param.valueTypeCode ?? null,
          defaultValue: param.defaultValue ?? null,
        })) ?? []
      )
    }

    return (
      objectOrSystemModel?.fields?.map(field => ({
        key: buildRowKey(field.name),
        name: field.name,
        description: field.desc ?? null,
        isRequired: field.required ?? null,
        canBeOverridden: null,
        valueTypeCode: field.valueType ?? null,
        defaultValue: null,
      })) ?? []
    )
  }, [parametersMode, commandParameters?.params, objectOrSystemModel?.fields])

  const jsValue: string =
    useWatch({
      control,
      name: jsRowIndex != null ? (`${name}.${jsRowIndex}.value` as any) : ('' as any),
    }) ?? ''

  const watchedItems = useWatch({ control, name: name as any }) as
    | CommandParametersType[]
    | undefined

  const isSchemaReady = !isLoading && availableRows.length > 0
  const isStructureReady = !isSchemaReady || (watchedItems?.length ?? 0) === availableRows.length

  const handleCloseScriptDialog = () => {
    setScriptDialogOpen(false)
    setJsRowIndex(null)
  }

  const handleSaveScriptDialog = (value: string) => {
    if (jsRowIndex == null) return

    setValue(`${name}.${jsRowIndex}.value` as any, value, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const handleOpenScriptDialog = useCallback((index: number) => {
    setJsRowIndex(index)
    setScriptDialogOpen(true)
  }, [])

  const handleBodyViewChange = useCallback((value: BODY_VIEW) => {
    setBodyView(value)
  }, [])

  useEffect(() => {
    if (!isSchemaReady) return
    if (!availableRows.length) return

    const currentItems = (getValues(name as any) ?? []) as CommandParametersType[]
    const mergedItems = mergeAvailableWithConfigured(availableRows, currentItems)

    const currentKeys = currentItems.map(item => String(item?.title?.id ?? ''))
    const mergedKeys = mergedItems.map(item => String(item?.title?.id ?? ''))

    const hasSameStructure =
      currentKeys.length === mergedKeys.length &&
      currentKeys.every((key, index) => key === mergedKeys[index])

    if (hasSameStructure) return

    resetField(name, {
      defaultValue: mergedItems,
      keepDirty: true,
    })

    clearErrors(name as any)
  }, [isSchemaReady, availableRows, name, getValues, reset, clearErrors, watchedItems?.length])

  const widthContent = label ? `calc(100% - ${labelWidth}px)` : '100%'

  if (commandParameters?.httpMethod === 'GET' && parametersMode === 'body') {
    return null
  }

  return (
    <Grid container sx={{ mb: 3, ...containerSx }}>
      {isScriptDialogOpen &&
        (jsScriptConfig ? (
          <NewScriptValueDialog
            {...jsScriptConfig}
            isShow={isScriptDialogOpen}
            value={jsValue}
            onClose={handleCloseScriptDialog}
            onSave={handleSaveScriptDialog}
          />
        ) : (
          <ScriptValueDialog
            windowBoundaries
            isShow={isScriptDialogOpen}
            modalType={MODAL_TYPE.EDIT}
            objectFields={[]}
            title={t('tooltips.title.editJs')}
            value={jsValue}
            onClose={handleCloseScriptDialog}
            onSave={handleSaveScriptDialog}
          />
        ))}

      {label && (
        <Grid item minWidth={labelWidth} width={labelWidth}>
          <Typography color='#616161' paddingY={1.5} variant='body1' width={labelWidth}>
            {label}
          </Typography>
        </Grid>
      )}

      <Grid item display='flex' flexGrow='1' width={widthContent}>
        <CommandConfigTable
          bodyView={parametersMode === 'body' ? bodyView : undefined}
          canChangeRefObject={canChangeRefObject}
          defaultRefFieldsObjectOption={defaultRefFieldsObjectOption}
          fieldArrayFields={fields}
          isDisabled={!commandCode}
          isFieldTypeDisabled={disableFieldType}
          isLoading={isLoading || !isStructureReady}
          mode={parametersMode}
          namePrefix={name}
          refObjectOptions={refObjectOptions}
          rows={availableRows}
          onBodyViewChange={handleBodyViewChange}
          onEditJs={handleOpenScriptDialog}
        />
      </Grid>
    </Grid>
  )
}
